import { useEffect, useRef, useState } from 'react';
import { Play, Square } from 'lucide-react';
import { ApiError } from '../api';
import { Chip, Note, Spinner } from './ui';

const SAMPLE = 'Namaste! Aapka order kal subah dus baje tak deliver ho jayega. Kuch aur madad chahiye?';

const VOICES = [
  { id: 'rumik', name: 'Rumik Silk', tag: 'Most affordable', tone: 'ok' as const, note: 'Built for Indian voices and Hinglish.' },
  { id: 'elevenlabs', name: 'ElevenLabs', tag: 'Premium', tone: 'accent' as const, note: 'Widely used global voice engine.' },
];

/** Same line, two engines. Lets a client hear the price/quality trade-off for themselves. */
export default function VoiceCompare() {
  const [text, setText] = useState(SAMPLE);
  const [busy, setBusy] = useState<string | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const [info, setInfo] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const urls = useRef<string[]>([]);

  useEffect(() => () => {
    audio.current?.pause();
    urls.current.forEach((u) => URL.revokeObjectURL(u));
  }, []);

  async function play(id: string) {
    if (playing === id) {
      audio.current?.pause();
      setPlaying(null);
      return;
    }
    audio.current?.pause();
    setError(null);
    setBusy(id);
    try {
      const res = await fetch('/api/tts/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: id, text }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new ApiError(res.status, data?.error?.code ?? 'error', data?.error?.message ?? 'Could not generate audio.');
      }
      const ms = Number(res.headers.get('X-Synthesis-Ms') || 0);
      const audioMs = Number(res.headers.get('X-Audio-Ms') || 0);
      const url = URL.createObjectURL(await res.blob());
      urls.current.push(url);
      setInfo((i) => ({ ...i, [id]: `Generated ${(audioMs / 1000).toFixed(1)}s of audio in ${ms} ms` }));
      const a = new Audio(url);
      audio.current = a;
      a.onended = () => setPlaying(null);
      setPlaying(id);
      await a.play();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not play the preview.');
      setPlaying(null);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="card" aria-labelledby="compare-title">
      <div className="card-head">
        <div>
          <h2 id="compare-title">Compare the voices</h2>
          <p>Type a line and hear it in each voice engine.</p>
        </div>
      </div>
      <label htmlFor="compare-text" className="sr-only">Text to speak</label>
      <textarea id="compare-text" value={text} maxLength={300} onChange={(e) => setText(e.target.value)} rows={3} />
      {error ? <div style={{ marginTop: 10 }}><Note tone="error">{error}</Note></div> : null}
      <div style={{ marginTop: 6 }}>
        {VOICES.map((v) => (
          <div className="voice-row" key={v.id}>
            <div>
              <div className="row" style={{ gap: 8 }}>
                <span className="name">{v.name}</span>
                <Chip tone={v.tone}>{v.tag}</Chip>
              </div>
              <div className="small muted">{info[v.id] ?? v.note}</div>
            </div>
            <button className="btn" onClick={() => play(v.id)} disabled={!text.trim() || (busy !== null && busy !== v.id)} aria-label={`${playing === v.id ? 'Stop' : 'Play'} ${v.name}`}>
              {busy === v.id ? <Spinner label="Generating" /> : playing === v.id ? <Square aria-hidden="true" /> : <Play aria-hidden="true" />}
              {playing === v.id ? 'Stop' : 'Play'}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
